import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { Progress } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX } from '../../../lib/zLayers';

export type FolderDeletePhase = 'deleting' | 'done';

export interface FolderDeleteProgressState {
  folderName: string;
  subfolderCount: number;
  templateCount: number;
  documentCount: number;
  phase: FolderDeletePhase;
}

interface TemplateFolderDeleteProgressModalProps {
  progress: FolderDeleteProgressState | null;
}

const TemplateFolderDeleteProgressModal: React.FC<TemplateFolderDeleteProgressModalProps> = ({
  progress,
}) => {
  const { colors } = useTheme();
  const [animatedPct, setAnimatedPct] = useState(12);

  useEffect(() => {
    if (!progress) return;
    if (progress.phase === 'done') {
      setAnimatedPct(100);
      return;
    }
    setAnimatedPct(12);
    const id = window.setInterval(() => {
      setAnimatedPct((prev) => (prev >= 92 ? 12 : prev + 1.8));
    }, 160);
    return () => window.clearInterval(id);
  }, [progress]);

  const { percent, statusLine, detailLine } = useMemo(() => {
    if (!progress) {
      return { percent: 0, statusLine: '', detailLine: '' };
    }
    if (progress.phase === 'done') {
      return {
        percent: 100,
        statusLine: 'Folder deleted',
        detailLine: 'Refreshing library…',
      };
    }
    const parts: string[] = [];
    if (progress.subfolderCount > 0) {
      parts.push(`${progress.subfolderCount} subfolder${progress.subfolderCount === 1 ? '' : 's'}`);
    }
    if (progress.templateCount > 0) {
      parts.push(`${progress.templateCount} template${progress.templateCount === 1 ? '' : 's'}`);
    }
    if (progress.documentCount > 0) {
      parts.push(`${progress.documentCount} document${progress.documentCount === 1 ? '' : 's'}`);
    }
    return {
      percent: animatedPct,
      statusLine: 'Deleting folder contents…',
      detailLine:
        parts.length > 0
          ? `Removing ${parts.join(', ')}`
          : 'Removing folder…',
    };
  }, [progress, animatedPct]);

  if (!progress) return null;

  return (
    <Modal isOpen closeOnEscape={false} onClose={() => {}} size="md" zIndex={Z_INDEX.modalHigh}>
      <ModalHeader>
        <ModalTitle>Deleting library folder</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-5">
          <div>
            <p className={cn('text-sm font-medium', colors.text)}>{progress.folderName}</p>
            <p className={cn('text-xs mt-0.5', colors.textMuted)}>
              This may take a moment for large folders.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className={cn('text-sm font-medium flex items-center gap-2', colors.text)}>
                {progress.phase === 'done' ? (
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" aria-hidden />
                ) : (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                )}
                {statusLine}
              </p>
              <span className={cn('text-sm tabular-nums', colors.textMuted)}>
                {Math.round(percent)}%
              </span>
            </div>
            <Progress value={percent} max={100} size="lg" />
            <p className={cn('text-xs', colors.textMuted)}>{detailLine}</p>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default TemplateFolderDeleteProgressModal;
